import React, { useState } from 'react';
import styled from 'styled-components';
import { FiShare2, FiCopy, FiCheck } from 'react-icons/fi';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaPinterest, FaTelegram } from 'react-icons/fa';
import { shareToSocialMedia, generateHashtags } from '../utils/socialMediaHelpers';

interface Blog {
  id: number;
  title: string;
  description: string;
  author?: string;
  image?: string;
  tags?: Array<{name: string; slug?: string; id?: number}> | string[];
}

interface SocialShareButtonsProps {
  blog: Blog;
  showTitle?: boolean;
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({ 
  blog, 
  showTitle = true 
}) => {
  const [hashtagsCopied, setHashtagsCopied] = useState(false);

  const handleCopyHashtags = () => {
    const hashtags = generateHashtags(blog.tags || []);
    navigator.clipboard.writeText(hashtags)
      .then(() => {
        setHashtagsCopied(true);
        setTimeout(() => setHashtagsCopied(false), 2000);
      })
      .catch(err => console.error('Failed to copy hashtags:', err));
  };

  return (
    <Container>
      {showTitle && <ShareTitle><FiShare2 /> Share this blog</ShareTitle>}
      
      <ButtonsGrid>
        <ShareButton 
          onClick={() => shareToSocialMedia('facebook', blog)}
          color="#1877f2"
          title="Share on Facebook"
        >
          <FaFacebook size={18} />
          <span>Facebook</span>
        </ShareButton>
        
        <ShareButton 
          onClick={() => shareToSocialMedia('twitter', blog)}
          color="#1da1f2"
          title="Share on Twitter/X"
        >
          <FaTwitter size={18} />
          <span>Twitter</span>
        </ShareButton>
        
        <ShareButton 
          onClick={() => shareToSocialMedia('linkedin', blog)}
          color="#0a66c2"
          title="Share on LinkedIn"
        >
          <FaLinkedin size={18} />
          <span>LinkedIn</span>
        </ShareButton>
        
        <ShareButton 
          onClick={() => shareToSocialMedia('whatsapp', blog)}
          color="#25d366"
          title="Share on WhatsApp"
        >
          <FaWhatsapp size={18} />
          <span>WhatsApp</span>
        </ShareButton>
        
        <ShareButton 
          onClick={() => shareToSocialMedia('pinterest', blog)}
          color="#e60023"
          title="Share on Pinterest"
        >
          <FaPinterest size={18} />
          <span>Pinterest</span>
        </ShareButton>
        
        <ShareButton 
          onClick={() => shareToSocialMedia('telegram', blog)}
          color="#0088cc"
          title="Share on Telegram"
        >
          <FaTelegram size={18} />
          <span>Telegram</span>
        </ShareButton>
      </ButtonsGrid>
      
      {blog.tags && blog.tags.length > 0 && (
        <HashtagsSection>
          <HashtagsLabel>Hashtags for this blog:</HashtagsLabel>
          <HashtagsText>
            {generateHashtags(blog.tags)}
          </HashtagsText>
          <CopyButton onClick={handleCopyHashtags}>
            {hashtagsCopied ? <FiCheck size={14} /> : <FiCopy size={14} />}
            <span>{hashtagsCopied ? 'Copied!' : 'Copy Hashtags'}</span>
          </CopyButton>
        </HashtagsSection>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const ShareTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
`;

const ButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const ShareButton = styled.button<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 2px solid ${props => props.color};
  color: ${props => props.color};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.color};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.color}33;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const HashtagsSection = styled.div`
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const HashtagsLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
`;

const HashtagsText = styled.div`
  font-size: 14px;
  color: #3b82f6;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #dbeafe;
  margin-bottom: 12px;
  word-wrap: break-word;
  line-height: 1.6;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

export default SocialShareButtons;




